import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useDebounce } from '@uidotdev/usehooks';

import { useAuth } from '../Services/AuthContext';
import { apiFetch } from "../utils/api";

import {
  Search,
  Download,
  Calculator,
  ShoppingCart,
  Package,
  Filter
} from 'lucide-react';
import { formatZARCurrency } from '../utils/formatUtils';
import { toast } from 'sonner';
import { fetchProductDescriptions } from '../Services/SalesRenewalsService';


export function PriceLookup({ setActiveSection, navigateToCreateOpportunity }) {
  const [priceSearchTerm, setPriceSearchTerm] = useState('');
  const debouncedPriceSearchTerm = useDebounce(priceSearchTerm, 500);
  const [selectedProductGroup, setSelectedProductGroup] = useState('all');
  const [selectedContractTerm, setSelectedContractTerm] = useState('all');
  const [selectedSKUType, setSelectedSKUType] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [priceList, setPriceList] = useState([]);
  const [filteredPriceList, setFilteredPriceList] = useState([]);
  const [uniqueProductGroups, setUniqueProductGroups] = useState([]);
  const [uniqueContractTerms, setUniqueContractTerms] = useState([]);
  const [uniqueSkuTypes, setUniqueSkuTypes] = useState([]);
  const {
    idToken,
    currentPartner,
    setPendingOpportunityItem,
    currentOpportunityId,
    setIsViewOpportunityOpen,
    setViewDialogEditData,
    viewDialogEditData,
    setEditOpportunityData,
    viewOpportunity,
    setIsViewDialogInEditMode
  } = useAuth();

  const fetchSkuTypes = async () => {
    let tk = await idToken();
    const res = apiFetch(tk, `/api/products/auto_desk_types`);
    res.then(async (response) => {
      setUniqueSkuTypes(response.data);
    });
  };

  useEffect(() => {
    const fetchProducts = async () => {
      let tk = await idToken();
      const res = apiFetch(tk, `/api/products?partner=${currentPartner.partner_id}`);
      res.then(async (response) => {
        setPriceList(response.data);
        setFilteredPriceList(response.data);
      });
    };
    fetchSkuTypes();
    fetchProducts();
    fetchProductDescriptions(idToken, setUniqueProductGroups);
  }, []);

  const fetchProducts = async () => {
    let tk = await idToken();
    let url = `/api/products?partner=${currentPartner.partner_id}&company_id=${currentPartner.company_id}`;
    if(selectedProductGroup !== 'all') {
      url += `&productGroup=${encodeURIComponent(selectedProductGroup)}`;
    }
    if(debouncedPriceSearchTerm !== 'all') {
      url += `&searchTerm=${encodeURIComponent(debouncedPriceSearchTerm)}`;
    }
    const res = apiFetch(tk, url);
    res.then(async (response) => {
      setPriceList(response.data);
      setFilteredPriceList(response.data);
      setUniqueContractTerms([
        ...new Set(
          response.data
            .map(item => item.contract_term)
            .filter(term => term && term.trim() !== "")
        )
      ].sort());
    });
  };

  useEffect(() => {
    fetchProducts();
  }, [debouncedPriceSearchTerm, currentPartner, selectedProductGroup]);


  useEffect(() => {
    const data = priceList.filter(item => {
      let matchesProductGroup = selectedProductGroup === 'all' || item.description === selectedProductGroup;
      let matchesContractTerm = selectedContractTerm === 'all' || item.contract_term === selectedContractTerm;
      let matchesSkuType = selectedSKUType === 'all' || item.autodesk_sku_type === selectedSKUType;
      return matchesProductGroup && matchesContractTerm && matchesSkuType;
    });
    setFilteredPriceList(data);
  }, [selectedProductGroup, selectedContractTerm, selectedSKUType])

  // Helper functions for badge colors
  const getSeatTypeColor = (seatType: string) => {
    switch (seatType) {
      case 'Commercial': return 'bg-worldsview-blue';
      case 'Educational': return 'bg-worldsview-green';
      case 'Government': return 'bg-worldsview-purple';
      default: return 'bg-gray-500';
    }
  };

  const getProductGroupColor = (productGroup: string) => {
    return 'bg-worldsview-blue';
  };

  const handlePriceExport = async () => {
    try {
      toast.info('Preparing export...', { description: 'Fetching all products for export' });
      
      let tk = await idToken();
      let url = `/api/products?partner=${currentPartner.partner_id}&company_id=${currentPartner.company_id}&size=0`;
      if(selectedProductGroup !== 'all') {
        url += `&productGroup=${encodeURIComponent(selectedProductGroup)}`;
      }
      if(debouncedPriceSearchTerm !== '' && debouncedPriceSearchTerm !== 'all') {
        url += `&searchTerm=${encodeURIComponent(debouncedPriceSearchTerm)}`;
      }
      
      const res = await apiFetch(tk, url);
      const allProducts = res.data;
      
      // Apply the same client-side filters that are applied to the displayed list
      const filteredData = allProducts.filter(item => {
        let matchesContractTerm = selectedContractTerm === 'all' || item.contract_term === selectedContractTerm;
        let matchesSkuType = selectedSKUType === 'all' || item.autodesk_sku_type === selectedSKUType;
        return matchesContractTerm && matchesSkuType;
      });
      
      const csvContent = "data:text/csv;charset=utf-8," +
        "SKU,Product Name,Description,Seat Type,SKU Type,Product Group,Contract Term,Unit Price\n" +
        filteredData.map(item =>
          `"${item.item_id || ''}","${item.product_name || ''}","${item.description || ''}","${item.autodesk_seat_type || ''}","${item.autodesk_sku_type || ''}","${item.item_group || ''}","${item.contract_term || ''}","${(formatZARCurrency(item.unit_price) || '').replace(/\u00A0/g, ' ')}"`
        ).join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "price_list_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Export complete', { description: `Exported ${filteredData.length} products` });
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed', { description: 'Please try again' });
    }
  };

  const handleAddToOpportunity = async (item) => {

    let qty = item.quantity ? item.quantity : 1;
    let it = {
      item_id: item.item_id,
      product_name: item.product_name,
      description: item.description,
      unit_price: item.unit_price,
      price: item.unit_price,
      amount: item.unit_price * qty,
      autodesk_seat_type: item.autodesk_seat_type,
      autodesk_sku_type: item.autodesk_sku_type,
      item_group: item.item_group,
      contract_term: item.contract_term,
      currency: 'ZAR',
      amountAfterMargin: item.unit_price,
      qty: qty,
      autodesk_months: item.contract_term
    }
    setPendingOpportunityItem(it);

    if (currentOpportunityId) {
      console.debug('Attempting to add item to opportunity:', viewOpportunity, viewDialogEditData, item);
      if (viewOpportunity.lines.length > 0) {
        const firstLineContractTerm = viewOpportunity.lines[0].autodesk_months;
        if (firstLineContractTerm !== item.contract_term) {
          toast.error('Product contract term mismatch', {
            description: `Only products with a term of '${firstLineContractTerm}' can be added to this opportunity. Existing products have a different term.`
          });
          return;
        }
      }

      let unit_price = item.unit_price;
      let months = null;
      if(viewDialogEditData.contract_end_date != null) {
        const period = item.contract_term === 'Annual' ? 12 : 36;
        const startDate = new Date(viewDialogEditData.transaction_start_date);
        const endDate = new Date(viewDialogEditData.contract_end_date);
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        months = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30.44)); // Average month length
        unit_price = (item.unit_price / period) * months;

        it.autodesk_months = months;
        it.unit_price = unit_price;
        it.price = unit_price;
        it.amount = unit_price * qty;
      }

      setIsViewOpportunityOpen(true);
      setIsViewDialogInEditMode(true);
      setViewDialogEditData((prev) => ({
        ...prev,
        lines: [...(prev.lines || []), it],
      }));
      setActiveSection('Sales & Renewals');
    } else {
      navigateToCreateOpportunity();
      console.log('Navigating to create opportunity with product:', item);
    }
  };

  const totalProducts = filteredPriceList.length;

  return (
    <div className="space-y-6 animate-fade-in-fast">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1>Price Lookup</h1>
            <p className="text-muted-foreground mt-2">
              Search and compare Autodesk product pricing
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-worldsview-blue" />
            <Badge className="bg-worldsview-blue text-white">
              {totalProducts} Products
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-worldsview-blue" />
            Autodesk Product Pricing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Search and Filters */}
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search products, item numbers, or descriptions..."
                  value={priceSearchTerm}
                  onChange={(e) => setPriceSearchTerm(e.target.value)}
                  className="pl-10 glass-input"
                />
              </div>
              <Button
                onClick={handlePriceExport}
                variant="outline"
                className="glass-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="product-line-description">Product Line Description</Label>
                <Select value={selectedProductGroup} onValueChange={setSelectedProductGroup}>
                  <SelectTrigger className="glass-input">
                    <SelectValue placeholder="All Product Lines" />
                  </SelectTrigger>
                  <SelectContent className="glass-modal">
                    <SelectItem value="all">All Product Lines</SelectItem>
                    {uniqueProductGroups.map(group => (
                      <SelectItem key={group} value={group}>{group}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="sku-type">Sku Type ID</Label>
                <Select value={selectedSKUType} onValueChange={setSelectedSKUType}>
                  <SelectTrigger className="glass-input">
                    <SelectValue placeholder="All SKU Types" />
                  </SelectTrigger>
                  <SelectContent className="glass-modal">
                    <SelectItem value="all">All</SelectItem>
                    {uniqueSkuTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="contract-term">Contract Term</Label>
                <Select value={selectedContractTerm} onValueChange={setSelectedContractTerm}>
                  <SelectTrigger className="glass-input">
                    <SelectValue placeholder="All Terms" />
                  </SelectTrigger>
                  <SelectContent className="glass-modal">
                    <SelectItem value="all">All Terms</SelectItem>
                    {uniqueContractTerms.map(term => (
                      <SelectItem key={term} value={term}>{term}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Results Section with Two-Panel Layout */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {filteredPriceList.length} of {priceList.length} products
              </p>
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {filteredPriceList.length !== priceList.length ? 'Filtered' : 'All'} Results
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[600px]">
              {/* Left Panel - Product Cards */}
              <div className="lg:col-span-2 space-y-2 overflow-y-auto pr-2">
                {filteredPriceList && filteredPriceList.map((item, index) => (
                  <Card
                    key={index}
                    className={`glass-card cursor-pointer transition-all ${selectedProduct?.itemNumber === item.itemNumber
                      ? 'ring-2 ring-worldsview-blue bg-worldsview-blue/5'
                      : 'hover:bg-muted/20'
                      }`}
                    onClick={() => setSelectedProduct(item)}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm mb-1 truncate">{item.product_name}</div>
                          <div className="text-xs text-muted-foreground font-mono mb-2 truncate">{item.item_id}</div>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge variant="outline" className="text-xs whitespace-nowrap">
                              {item.contract_term}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={`${getProductGroupColor(item.autodesk_product_group_id)} text-white border-transparent text-xs whitespace-nowrap`}
                            >
                              {item.autodesk_sku_type}
                            </Badge>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-semibold text-worldsview-green whitespace-nowrap">{formatZARCurrency(item.unit_price)}</div>
                          <div className="text-xs text-muted-foreground mt-1">{item.description}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {filteredPriceList.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-muted/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="font-semibold mb-2">No products found</h3>
                    <p className="text-muted-foreground">
                      Try adjusting your search criteria or filters.
                    </p>
                  </div>
                )}
              </div>

              {/* Right Panel - Product Details */}
              <div className="lg:col-span-1">
                {selectedProduct ? (
                  <Card className="glass-card h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Package className="w-4 h-4 text-worldsview-blue" />
                        Product Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 overflow-y-auto max-h-[520px] pr-2">
                      {/* Product Name */}
                      <div>
                        <Label className="text-xs text-muted-foreground">Product Name</Label>
                        <div className="font-medium text-sm leading-tight">{selectedProduct.product_name}</div>
                      </div>

                      {/* SKU */}
                      <div>
                        <Label className="text-xs text-muted-foreground">SKU</Label>
                        <div className="font-mono text-sm break-all">{selectedProduct.item_id}</div>
                      </div>

                      {/* Description */}
                      <div>
                        <Label className="text-xs text-muted-foreground">Product Line Description</Label>
                        <div className="text-sm leading-tight">{selectedProduct.description}</div>
                      </div>

                      {/* Price */}
                      <div className="p-3 bg-worldsview-green/5 rounded-lg border border-worldsview-green/20">
                        <Label className="text-xs text-muted-foreground">Unit Price</Label>
                        <div className="text-lg font-bold text-worldsview-green"> {formatZARCurrency(selectedProduct.unit_price)}</div>
                      </div>

                      {/* Number of Seats Calculator */}
                      <div className="space-y-2">
                        <Label htmlFor="seats-calc" className="text-xs text-muted-foreground">Number of Seats</Label>
                        <Input
                          id="seats-calc"
                          type="number"
                          min="1"
                          defaultValue="1"
                          className="glass-input"
                          onChange={(e) => {
                            const quantity = parseInt(e.target.value) || 1;
                            const unitPrice = parseFloat(selectedProduct.unit_price.replace(',', ''));
                            const total = unitPrice * quantity;
                            const totalElement = document.getElementById('total-price');
                            if (totalElement) {
                              totalElement.textContent = formatZARCurrency(total);
                            }
                            selectedProduct.quantity = quantity;
                          }}
                        />
                        <div className="flex justify-between items-center pt-2 border-t border-border/30">
                          <span className="text-xs text-muted-foreground">Total Price</span>
                          <span id="total-price" className="font-semibold text-worldsview-blue">{formatZARCurrency(selectedProduct.unit_price)}</span>
                        </div>
                      </div>

                      {/* Contract & Product Info Grid */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-muted-foreground">Contract Term</Label>
                          <Badge variant="outline" className="mt-1 text-xs">
                            {selectedProduct.contract_term}
                          </Badge>
                        </div>
                        {/* <div>
                          <Label className="text-xs text-muted-foreground">Currency</Label>
                          <div className="text-sm font-medium mt-1">ZAR</div>
                        </div> */}
                      </div>

                      {/* Type & Classification */}
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">SKU Type ID</Label>
                          <div className="mt-1">
                            <Badge
                              variant="outline"
                              className={`${getSeatTypeColor(selectedProduct.seatTypeId)} text-white border-transparent text-xs`}
                            >
                              {selectedProduct.autodesk_sku_type}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Action Button - Sticky at bottom */}
                      <div className="sticky bottom-0 bg-card/95 backdrop-blur-sm pt-3 -mx-6 px-6 pb-6 mt-4 border-t border-border/30">
                      {selectedProduct && selectedProduct.autodesk_sku_type === 'M2S New' || selectedProduct.autodesk_sku_type === 'Subscription New' &&
                      <Button
                          onClick={() => handleAddToOpportunity(selectedProduct)}
                          className="w-full bg-worldsview-green hover:bg-worldsview-green/90 text-white"
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Add to Opportunity
                        </Button>
                      }
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="glass-card h-full flex items-center justify-center">
                    <CardContent className="text-center p-8">
                      <div className="w-16 h-16 bg-muted/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Package className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="font-semibold mb-2">Select a Product</h3>
                      <p className="text-muted-foreground text-sm">
                        Click on any product card to view detailed information and pricing options.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}